import type { YAxisRule } from './measures';

/** A closed numeric range [min, max]. */
export type Extent = readonly [number, number];

/** Min and max across several arrays, by loop so 100k+ values can't overflow the call stack. */
export const extentOf = (
  arrays: readonly (readonly number[])[]
): Extent | null => {
  let min = Infinity;
  let max = -Infinity;
  for (const values of arrays) {
    for (let i = 0; i < values.length; i += 1) {
      const value = values[i];
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }
  return min <= max ? [min, max] : null;
};

/** A chart's y-axis domain from its rule and the visible values; a flat or empty series still gets a span. */
export const yDomain = (rule: YAxisRule, values: Extent | null): Extent => {
  if (rule.kind === 'clamp') {
    if (!values) return [rule.min, rule.max];
    const low = Math.max(rule.min, Math.min(values[0], rule.max));
    const high = Math.min(rule.max, Math.max(values[1], rule.min));
    if (low < high) return [low, high];
    if (high >= rule.max) return [rule.max - 1, rule.max];
    if (low <= rule.min) return [rule.min, rule.min + 1];
    return [low - 1, high + 1];
  }
  if (rule.kind === 'fromZero') {
    const top = values ? Math.max(values[1], 0) : 0;
    return [0, top > 0 ? top : 1];
  }
  let min = values ? values[0] : Infinity;
  let max = values ? values[1] : -Infinity;
  if (rule.include) {
    min = Math.min(min, rule.include[0]);
    max = Math.max(max, rule.include[1]);
  }
  if (min > max) return [0, 1];
  return min === max ? [min - 1, max + 1] : [min, max];
};
